import { useCallback, useState } from 'react';

import type { ZodType } from 'zod';

import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { ContractModalSource } from '#src/app/pages/ambassadors/landing/pageContext';

import { useContractFormPerimeterXCaptcha } from '../useContractFormPerimeterXCaptcha';

import { createContractFormSchema } from './createContractFormSchema';
import type { ContractFormValues } from './submitContractRequest';
import { submitContractRequest } from './submitContractRequest';

export type { ContractFormValues } from './submitContractRequest';

export type ContractFormView = 'form' | 'thanks';

type Result = ModelHookResult<
	{
		view: ContractFormView;
		schema: ZodType<ContractFormValues>;
		defaultValues: ContractFormValues;
		submitError: boolean;
	},
	{
		submit: (values: ContractFormValues) => Promise<void>;
	}
>;

export function useContractForm(source: ContractModalSource | null): Result {
	const { translate } = useI18n();
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const [view, setView] = useState<ContractFormView>('form');
	const [submitError, setSubmitError] = useState(false);
	const triggerPerimeterXCaptcha = useContractFormPerimeterXCaptcha();

	const schema = createContractFormSchema(translate);

	// `isRetry` caps this at one re-submit, so a repeatedly-failing challenge can't trap the user.
	const submit = useCallback(
		async function submitContractFormValues(values: ContractFormValues, isRetry = false): Promise<void> {
			try {
				await submitContractRequest(utilityContext, values, source);
				void track('ambassadors_contract_form_submit', { source_location: source ?? undefined });
				setSubmitError(false);
				setView('thanks');
			} catch (err) {
				if (!isRetry && err instanceof GQLResponsePerimeterXChallengeError) {
					const solved = await triggerPerimeterXCaptcha({ pxResponse: err.pxChallenge });
					if (solved) {
						await submitContractFormValues(values, true);
						return;
					}
				}
				// Only two reasons are distinguishable here: submitContractRequest deliberately throws a
				// generic Error for the mutation's own `Failure` member, so the server's code
				// (RATE_LIMITED, HUBSPOT_*) never reaches the client. Pairs with the success event so the
				// submit success rate is computable, and separates applicants lost to an unsolved
				// challenge from real submission failures.
				void track('ambassadors_contract_form_submit_error', {
					source_location: source ?? undefined,
					error_reason:
						err instanceof GQLResponsePerimeterXChallengeError ? 'perimeterx_unsolved' : 'submission_failed',
				});
				setSubmitError(true);
			}
		},
		[utilityContext, source, track, triggerPerimeterXCaptcha],
	);

	return {
		data: {
			view,
			schema,
			defaultValues: {
				name: '',
				email: '',
				handle: '',
				petitionIdea: '',
				videoLink: '',
				notSure: false,
			},
			submitError,
		},
		actions: {
			submit,
		},
	};
}
